import { z } from 'zod';
import { IsoDateTime, UuidString } from '../../primitives.js';

/**
 * Timer — таймер задачи (B18/B20, work-tasks-user-feedback §3.8).
 *
 * Строка существует, только пока идёт работа: стоп пишет запись в журнал и
 * УДАЛЯЕТ таймер. Поэтому «активен» — это факт наличия строки, а не поле.
 *
 * Параллельность (решение владельца В-4): у одного человека может бежать
 * несколько таймеров на разных задачах; запрещён только второй таймер на той же
 * задаче. Следствие осознанное — сумма учтённого времени может превысить
 * астрономическое (SOLUTION §6.2).
 *
 * Клиентский тик — только отображение. Истина — сервер: FE считает секунды от
 * `started_at` + `accumulated_seconds`, но записывает время исключительно стоп
 * на сервере.
 */

export const TimerStatus = z.enum(['running', 'paused']);
export type TimerStatus = z.infer<typeof TimerStatus>;

export const Timer = z.object({
  id: UuidString,
  entity_id: UuidString,
  task_id: UuidString,
  user_id: UuidString,
  /** NULL ⇔ пауза: момент начала неучтённого отрезка. */
  started_at: IsoDateTime.nullable(),
  /** Учтённое до текущего отрезка, целые секунды. */
  accumulated_seconds: z.number().int().nonnegative(),
  status: TimerStatus,
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
  // ── Денорм для индикатора в шапке (F29) ──────────────────────────
  // `null` — задача недоступна (RLS/архив): индикатор показывает таймер без
  // ссылки, а не глотает его. Молча пропасть таймер не имеет права — он тикает
  // и пишет время.
  /** Заголовок задачи. */
  task_title: z.string().nullable(),
  /** `sequence_id` задачи (для `#seq`). */
  task_seq: z.number().int().nullable(),
  /** Код проекта задачи (для `<код>-<seq>`). */
  project_code: z.string().nullable(),
});
export type Timer = z.infer<typeof Timer>;

/** `GET /task-module/timers` — свои активные таймеры в активной компании. */
export const TimerListResponse = z.object({
  timers: z.array(Timer),
});
export type TimerListResponse = z.infer<typeof TimerListResponse>;

/**
 * Ответ стопа. `worklog_created: false` — не ошибка: отрезок короче минуты
 * осознанно не попадает в журнал (§3.8), и FE обязан сказать об этом человеку,
 * а не молча показать пустой журнал.
 */
export const TimerStopResponse = z.object({
  worklog_created: z.boolean(),
  /** Сколько секунд насчитал сервер (для тоста и сверки с клиентским тиком). */
  total_seconds: z.number().int().nonnegative(),
  /** Почему записи нет. NULL ⇔ запись создана. */
  reason: z.enum(['too_short']).nullable(),
  /** id созданной записи журнала (NULL, если её нет). */
  worklog_id: UuidString.nullable(),
});
export type TimerStopResponse = z.infer<typeof TimerStopResponse>;

/** Порог, ниже которого стоп не создаёт запись журнала. Общий для BE и FE-текста. */
export const TIMER_MIN_WORKLOG_SECONDS = 60;

/**
 * B21 — сколько часов непрерывного бега считаются «человек забыл остановить».
 *
 * Константа, а не календарное «в конце рабочего дня»: надёжной таймзоны
 * пользователя в системе нет (§6.2), и «конец дня» пришлось бы угадывать.
 */
export const TIMER_REMINDER_THRESHOLD_HOURS = 8;

/** B21 — не чаще раза в сутки на один таймер: напоминание не должно стать шумом. */
export const TIMER_REMINDER_REPEAT_HOURS = 24;
